import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, RefreshCw, Plus, Upload, Loader2, Share2, Link2, Boxes, Gauge } from "lucide-react";
import api from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { CampaignReport } from "@/components/CampaignReport";
import { AddPostDialog } from "@/components/dialogs/AddPostDialog";
import { ImportCsvDialog } from "@/components/dialogs/ImportCsvDialog";
import { ShareDialog } from "@/components/dialogs/ShareDialog";
import { CreatorCoreSourceDialog } from "@/components/dialogs/CreatorCoreSourceDialog";
import { DASH_ZERO_CAMPAIGN_ID } from "@/lib/dashZeros";
import { SoundmeSync } from "@/components/SoundmeSync";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export default function Dashboard() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [addPost, setAddPost] = useState(false);
  const [importCsv, setImportCsv] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [ccOpen, setCcOpen] = useState(false);
  const [sync, setSync] = useState(null);

  const load = () => api.get(`/campaigns/${campaignId}`).then((r) => {
    setData(r.data);
    if (r.data?.campaign?.source_sync) setSync(r.data.campaign.source_sync);
  }).catch(() => toast.error("Failed to load campaign"));
  useEffect(() => { load(); }, [campaignId]);

  // Poll background source-sync status so the pull isn't tied to an open request/dialog.
  useEffect(() => {
    if (sync?.status !== "running") return;
    let active = true;
    const t = setInterval(async () => {
      try {
        const { data: d } = await api.get(`/campaigns/${campaignId}/sync-status`);
        if (!active) return;
        const ss = d.source_sync || { status: "idle" };
        setSync(ss);
        if (ss.status !== "running") {
          clearInterval(t);
          await load();
          if (ss.status === "done") toast.success(ss.message);
          else if (ss.status === "error") toast.error(ss.message);
        }
      } catch { /* keep polling */ }
    }, 3000);
    return () => { active = false; clearInterval(t); };
  }, [sync?.status, campaignId]);

  const camp = data?.campaign;

  const refresh = async () => {
    setRefreshing(true);
    try {
      await api.post(`/campaigns/${campaignId}/refresh`);
      await load();
      toast.success("Stats refreshed");
    } catch { toast.error("Refresh failed"); }
    finally { setRefreshing(false); }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/campaigns/${campaignId}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${camp.title.replace(/\s+/g, "_").toLowerCase()}_posts.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export ready");
    } catch { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  const shareUrl = `${window.location.origin}/share/${campaignId}`;

  const refreshSource = async () => {
    try {
      const r = await api.post(`/campaigns/${campaignId}/refresh-source`);
      setSync({ status: "running", kind: "refresh", message: r.data?.message || "Refreshing…" });
      toast.message(r.data?.message || "Refreshing from source link…");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start the refresh");
    }
  };

  const onCcStarted = (resp) => {
    setSync({ status: "running", kind: "creatorcore", message: resp?.message || "Pulling Creator Core posts…" });
    toast.message(resp?.message || "Pulling Creator Core posts…");
  };

  const syncing = sync?.status === "running";

  const hasSource = Boolean(camp?.imported_from && camp?.import_provider);
  const hasCcSource = Boolean(camp?.creatorcore_url);
  const canRefresh = hasSource || hasCcSource;
  const lastSynced = camp?.source_last_synced ? new Date(camp.source_last_synced) : null;
  const syncRes = camp?.source_last_sync_result;

  return (
    <div className="min-h-screen">
      <TopBar
        right={
          <Button data-testid="export-posts-btn" onClick={exportCsv} disabled={exporting || !camp} variant="outline" className="rounded-full border-neutral-300 bg-white hover:bg-neutral-50">
            {exporting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}
            Export Posts
          </Button>
        }
      />

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <button data-testid="back-btn" onClick={() => camp && navigate(camp.song_id ? `/songs/${camp.song_id}` : `/artists/${camp.artist_id}`)} className="mb-6 flex items-center gap-1.5 text-sm text-neutral-500 transition-colors hover:text-black">
          <ArrowLeft className="h-4 w-4" /> Back to song
        </button>

        {!data ? (
          <div className="space-y-6">
            <Skeleton className="h-40 rounded-2xl" />
            <div className="grid grid-cols-3 gap-6"><Skeleton className="h-40 rounded-2xl" /><Skeleton className="h-40 rounded-2xl" /><Skeleton className="h-40 rounded-2xl" /></div>
          </div>
        ) : (
          <>
            <div className="mb-4 flex flex-wrap gap-2">
              <Button data-testid="refresh-stats-btn" onClick={refresh} disabled={refreshing} className="rounded-full bg-[#35606E] hover:bg-[#294b57]">
                <RefreshCw className={`mr-1.5 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh stats
              </Button>
              <Button data-testid="copy-share-btn" onClick={() => setShareOpen(true)} variant="outline" className="rounded-full border-neutral-300 bg-white hover:bg-neutral-50">
                <Share2 className="mr-1.5 h-4 w-4" /> Share
              </Button>
              <Button data-testid="exposure-check-btn" onClick={() => navigate(`/campaigns/${campaignId}/exposure`)} variant="outline" className="rounded-full border-neutral-300 bg-white hover:bg-neutral-50"><Gauge className="mr-1.5 h-4 w-4" /> Exposure Check</Button>
              <Button data-testid="add-post-btn" onClick={() => setAddPost(true)} variant="outline" className="rounded-full border-neutral-300 bg-white hover:bg-neutral-50">
                <Plus className="mr-1.5 h-4 w-4" /> Add post by URL
              </Button>
              <Button data-testid="import-csv-btn" onClick={() => setImportCsv(true)} variant="outline" className="rounded-full border-neutral-300 bg-white hover:bg-neutral-50">
                <Upload className="mr-1.5 h-4 w-4" /> Import CSV
              </Button>
              <Button data-testid="creatorcore-link-btn" onClick={() => setCcOpen(true)} disabled={syncing} variant="outline" className="rounded-full border-neutral-300 bg-white hover:bg-neutral-50">
                <Boxes className="mr-1.5 h-4 w-4" /> {hasCcSource ? "Creator Core link" : "Add Creator Core link"}
              </Button>
              {canRefresh && (
                <Button data-testid="refresh-source-btn" onClick={refreshSource} disabled={syncing} variant="outline" className="rounded-full border-neutral-300 bg-white hover:bg-neutral-50">
                  {syncing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Link2 className="mr-1.5 h-4 w-4" />}
                  Refresh from source link
                </Button>
              )}
            </div>

            {syncing && (
              <p data-testid="source-sync-running" className="mb-4 -mt-1 flex items-center gap-1.5 text-xs font-medium text-[#35606E]">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> {sync?.message || "Syncing from source link… This can take up to a minute."}
              </p>
            )}
            {!syncing && sync?.status === "error" && (
              <p data-testid="source-sync-error" className="mb-4 -mt-1 text-xs font-medium text-red-600">
                {sync?.message || "The last sync didn't complete. Please try again."}
              </p>
            )}
            {!syncing && canRefresh && lastSynced && (
              <p data-testid="source-last-synced" className="mb-4 -mt-1 text-xs text-neutral-500">
                Last synced {lastSynced.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
                {syncRes ? ` · updated ${syncRes.updated}, added ${syncRes.added}` : ""}
              </p>
            )}

            {!camp?.promote && camp?.source_provider !== "promote.fun" && (
              <SoundmeSync campaignId={campaignId} onDone={load} />
            )}

            <CampaignReport data={data} editable dashZeros={campaignId === DASH_ZERO_CAMPAIGN_ID} />
          </>
        )}
      </main>

      <AddPostDialog open={addPost} onOpenChange={setAddPost} campaignId={campaignId} onDone={load} />
      <ImportCsvDialog open={importCsv} onOpenChange={setImportCsv} campaignId={campaignId} onDone={load} />
      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} url={shareUrl} type="campaign" id={campaignId} />
      <CreatorCoreSourceDialog open={ccOpen} onOpenChange={setCcOpen} campaignId={campaignId} current={camp?.creatorcore_url} onStarted={onCcStarted} />
    </div>
  );
}
